import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatErr } from "../api";
import { toast } from "sonner";
import { Leaf } from "lucide-react";
import { trackEvent } from "../lib/analytics";
import Seo from "../components/Seo";
import { GoogleSignInButton, OrDivider } from "../components/GoogleSignInButton";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next") || "/my-backyard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await login(email, password);
      trackEvent("login", { method: "password" });
      toast.success("Welcome back!");
      nav(nextPath);
    } catch (e) {
      const msg = formatErr(e);
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex" data-testid="login-page">
      <Seo
        title="Sign In — Backyard Buddy"
        description="Sign in to your Backyard Buddy account to access your saved designs, projects, and Linda's garden planning tools."
        path="/login"
      />
      <div className="hidden md:block md:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1444392061186-9fc38f84f726?auto=format&fit=crop&w=1200&q=80"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A2F25]/40 to-[#1A2F25]/10" />
        <div className="absolute bottom-12 left-12 text-[#F9F6F0] max-w-md">
          <p className="overline text-[#D47C59] mb-3">Welcome back, gardener</p>
          <p className="font-serif text-4xl font-light leading-tight">
            "The garden suggests there might be a place where we can meet nature halfway."
          </p>
          <p className="text-sm font-light mt-4 text-[#F9F6F0]/70">— Michael Pollan</p>
        </div>
      </div>

      <div className="w-full md:w-1/2 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <div className="w-10 h-10 rounded-full bg-[#2D4A3E] flex items-center justify-center">
              <Leaf className="w-5 h-5 text-[#F9F6F0]" strokeWidth={1.5} />
            </div>
            <span className="font-serif text-2xl font-medium">Backyard Buddy</span>
          </Link>

          <p className="overline text-[#D47C59] mb-2">Sign in</p>
          <h1 className="font-serif text-4xl font-light mb-8">Welcome back.</h1>

          <GoogleSignInButton label="Continue with Google" source="login" />
          <OrDivider />

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="overline text-[#5C6B62] mb-2 block">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bb-input"
                placeholder="you@garden.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="overline text-[#5C6B62] mb-2 block">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bb-input"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>

            {err && <p className="text-sm text-red-700 font-light">{err}</p>}

            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="login-submit-button">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 flex flex-col gap-2 text-sm text-[#5C6B62] font-light">
            <Link to="/forgot-password" className="text-[#2D4A3E] hover:text-[#D47C59]" data-testid="login-forgot-link">
              Forgot your password?
            </Link>
            <p>
              New here?{" "}
              <Link to="/register" className="text-[#2D4A3E] underline underline-offset-4 hover:text-[#D47C59]" data-testid="login-to-register-link">
                Create an account
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
